import * as data from "@frui.ts/data";
import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import type { EntityNames } from "config";
import type PayGateToTransferRefundDetailResponse from "entities/payGateToTransferRefundDetailResponse";
import type PayGateToTransferRefundResponse from "entities/payGateToTransferRefundResponse";
import RefundTransferPaymentResponse from "entities/refundTransferPaymentResponse";
import PayGateToTransferRefundPaymentTemplate from "entities/payGateToTransferRefundPaymentTemplate";
import { computed } from "mobx";
import RefundRepository from "repositories/refundRepository";
import EventBus, { payGateToTransferRefundUpdated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import OptionsService from "services/optionsService";
import AppRouter from "services/router";
import SecurityService from "services/securityService";
import RefundsApprovalService from "services/refundsApprovalService";
import EnumService from "services/enumService";
import AlertsViewModel from "../../../alertsViewModel";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import EditableRefundDetailViewModel from "../detail/editableRefundDetailViewModel";
import TransferPaymentType from "entities/transferPaymentType";
import FeeType from "entities/feeType";
import PriceListService from "services/priceListService";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: ":refundId", name: Router.Self })
export default class PayGateToTransferRefundDetailViewModel extends EditableRefundDetailViewModel<
  PayGateToTransferRefundDetailResponse,
  PayGateToTransferRefundResponse
> {
  readonly modelName: EntityNames = "payGateToTransferRefund";

  @computed
  get showSEPAWarning() {
    const countryCode = this.item.refundPayment.counterpartyBank.countryCode ?? "";
    const isSepaMember = this.enumService.values("countries").find(item => item.code === countryCode)?.isSepaMember ?? false;

    return this.item.refundPayment.transferType === TransferPaymentType.Foreign && isSepaMember && this.isPaymentTypeEditable;
  }

  constructor(
    protected itemId: string,
    protected pagingFilter: data.IPagingFilter,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected repository: RefundRepository,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      repository,
      optionsService,
      securityService,
      eventBus,
      countrySelectViewModelFactory,
      enumService,
      priceListService,
      alertsViewModel
    );
  }

  @watchBusy
  protected fetchDetail(): Promise<PayGateToTransferRefundDetailResponse> {
    return this.repository.getPayGateToTransferRefund(this.itemId);
  }

  @watchBusy
  public fetchData() {
    const { limit, offset } = this.pagingFilter;

    return this.repository.getAllPayGateToTransferRefund({ offset, limit });
  }

  @watchBusy
  protected updatePayment(id: string, data: RefundTransferPaymentResponse): Promise<any> {
    return this.repository.updatePayGateToTransferRefundPayment(id, data);
  }

  @bound
  async onRefundSaved() {
    this.eventBus.publish(payGateToTransferRefundUpdated());
    await this.loadDetail();
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService
        .entityOptions(PayGateToTransferRefundPaymentTemplate)
        .find(option => option.id === refundPaymentTemplateId)?.label ?? emptyValue
    );
  }

  static Factory({ container }: interfaces.Context) {
    return (refundId: string, pagingFilter: data.IPagingFilter) =>
      new PayGateToTransferRefundDetailViewModel(
        refundId,
        pagingFilter,
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(RefundsApprovalService),
        container.get(RefundRepository),
        container.get(OptionsService),
        container.get(SecurityService),
        container.get(EventBus),
        container.get(EnumService),
        container.get(PriceListService),
        container.get(CountrySelectViewModel.Factory),
        container.get(AlertsViewModel)
      );
  }
}
